import { Link } from 'react-router-dom';
import { FileText, ArrowRight, Cpu, Binary } from 'lucide-react';

const ResearchPaper = ({ title, date, category, excerpt, tags, slug }: { title: string; date: string; category: string; excerpt: string; tags: string[]; slug: string }) => (
  <div className="group p-8 glass-dark rounded-3xl border border-white/5 hover:border-teal-500/30 transition-all duration-500 relative overflow-hidden h-full flex flex-col">
    <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-20 transition-opacity pointer-events-none">
      <FileText className="w-24 h-24 text-teal-400" />
    </div>
    <div className="relative z-10 text-left flex flex-col h-full">
      <div className="flex items-center gap-3 mb-6">
        <span className="px-3 py-1 bg-teal-500/10 text-teal-400 text-[10px] font-bold uppercase tracking-wider rounded-lg border border-teal-500/20 font-mono">
          {category}
        </span>
        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest font-mono">{date}</span>
      </div>
      <h3 className="text-xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors leading-tight italic uppercase tracking-tight">
        {title}
      </h3>
      <p className="text-slate-400 text-sm leading-relaxed mb-6 line-clamp-3 italic flex-1 capitalize">
        "{excerpt}"
      </p>
      <div className="flex flex-wrap gap-2 mb-8">
        {tags.slice(0, 2).map((tag, idx) => (
          <span key={idx} className="text-[10px] text-slate-400 border border-white/5 px-2 py-1 rounded-md">
            #{tag}
          </span>
        ))}
      </div>
      <Link to={`/research/${slug}`} className="flex items-center gap-2 text-white font-bold text-xs uppercase tracking-widest group/btn mt-auto pt-6 border-t border-white/5">
        <span>View Methodology</span>
        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform text-teal-500" />
      </Link>
    </div>
  </div>
);

export default function HomeResearchSection() {
  const papers = [
    {
      title: "The Psychometrics of Behavioral Signal Analysis",
      date: "March 2024",
      category: "Psychometrics",
      excerpt: "Moving beyond static answers to measure latent intent. How AI detects verbal stalls and cognitive load markers to differentiate memorized answers from genuine expertise.",
      tags: ["Signal Analysis", "NLP", "Cognitive Load"],
      slug: "psychometrics-ai-interviews"
    },
    {
      title: "Quantifying the 48-Hour Hiring Loop: A Statistical Model",
      date: "February 2024",
      category: "Operations",
      excerpt: "Findings show a 92% reduction in time-to-hire for Tier 1 engineering roles via automated technical verification vs. manual grading.",
      tags: ["Efficiency", "Automation", "Data Science"],
      slug: "48-hour-hiring-loop"
    },
    {
      title: "Mapping Senior-Level Mental Models via N-Step Inference",
      date: "January 2024",
      category: "Structural Reasoning",
      excerpt: "Benchmarking the ability to map a candidate's mental architecture (how they build a system) versus just checking if the code runs.",
      tags: ["Inference", "Skill Mapping", "System Design"],
      slug: "zero-shot-skill-verification"
    }
  ];

  return (
    <section className="py-24 bg-transparent relative overflow-hidden border-t border-white/5">
      {/* AI System Animation Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-[0.03]">
          <div className="absolute inset-0 border border-teal-500 rounded-full animate-ping-slow"></div>
          <div className="absolute inset-20 border border-teal-500/50 rounded-full animate-ping-slow [animation-delay:1s]"></div>
          <div className="absolute inset-40 border border-teal-500/30 rounded-full animate-ping-slow [animation-delay:2s]"></div>
        </div>
        
        {/* Floating AI Nodes */}
        <div className="absolute top-20 right-[10%] animate-float opacity-10">
          <Cpu className="w-16 h-16 text-teal-400" />
        </div>
        <div className="absolute bottom-20 left-[5%] animate-float-delayed opacity-5">
          <Binary className="w-24 h-24 text-emerald-400" />
        </div>
      </div>

      <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row items-end justify-between mb-24 gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/10 text-teal-400 text-[10px] font-bold uppercase tracking-[0.2em] rounded-full border border-teal-500/20 mb-4 font-mono">
              <FileText className="w-3 h-3" />
              Technical Archive
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tighter uppercase italic">
              Behavioral <span className="text-teal-400">Research</span> Lab
            </h2>
            <p className="text-slate-400 mt-4 leading-relaxed font-medium">
               Deep analytics on the science of skill verification — the Reason patterns and latency markers that truly define competency.
            </p>
          </div>
          <Link 
            to="/research" 
            className="group flex items-center gap-2 text-slate-400 hover:text-white transition-colors font-bold uppercase tracking-widest text-xs"
          >
            Access All Lab Files
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-teal-500" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {papers.map((paper, i) => (
            <ResearchPaper key={i} {...paper} />
          ))}
        </div>
      </div>
    </section>
  );
}
